import {
  AgendaSlotReservationApi,
  BookAgendaSlotOccurenceApiPayload,
  HttpErrors,
  SlotReservationDoctorSubstitute,
} from '@omecare/utils';
import { UseMutationResult, useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { z } from 'zod';
import { getErrorPayload } from '@/utils/errors/get-error-data';
import { useSlotBookingConflitErrorHandler } from '@/utils/errors/use-slot-booking-conflit.error-handler';
import http from '@/utils/http.service';
import { agendaSlotReservationSchema } from '../queries/use-get-office-agendas-slots';
import { transformApiOccurencePeriod } from './use-book-agenda-slot';

const bookAgendaSlotResponseSchema = z.object({
  id: z.string().uuid(),
  reservations: z.array(agendaSlotReservationSchema),
});

export type BookAgendaSlotAdminResponse = z.infer<
  typeof bookAgendaSlotResponseSchema
>;
export type BookAgendaSlotAdminPayload = {
  id: string;
  doctorSubstituteId: string;
  tag?: string;
  occurrence?: Omit<BookAgendaSlotOccurenceApiPayload, 'endAt'> & {
    endAt: Date | null;
  };
};

const bookAgendaSlotAdmin = (payload: BookAgendaSlotAdminPayload) => {
  let occurencePeriod: {
    startAt: Date;
    endAt: Date;
  } | null = null;

  if (payload.occurrence) {
    occurencePeriod = transformApiOccurencePeriod(payload.occurrence);
  }

  return http.agendaSlots
    .bookById(payload.id, {
      doctorSubstituteId: payload.doctorSubstituteId,
      tag: payload.tag,
      occurrence: payload.occurrence
        ? {
            ...payload.occurrence,
            ...occurencePeriod!,
          }
        : undefined,
    })
    .then<BookAgendaSlotAdminResponse>(({ response, transform }) => {
      const result = transform.handle({
        response,
        attributes: {},
        relations: {
          reservations: ({
            entity,
          }: {
            entity: AgendaSlotReservationApi;
          }): BookAgendaSlotAdminResponse['reservations'] => {
            const doctorSubstitute =
              transform.getApiEntityRelation<SlotReservationDoctorSubstitute>({
                relation: 'doctorSubstitute',
                relationships: entity.relationships,
                included: response.included,
              });

            return [
              {
                id: entity.id,
                status: entity.attributes.status,
                tag: entity.attributes.tag,
                doctorSubstitute: {
                  id: doctorSubstitute!.id,
                  firstname: doctorSubstitute!.attributes.firstname,
                  lastname: doctorSubstitute!.attributes.lastname,
                  picture: doctorSubstitute!.attributes.picture,
                  installable: doctorSubstitute!.attributes.installable,
                  hasPhysicalSiteAccess: false,
                },
              },
            ];
          },
        },
        schema: bookAgendaSlotResponseSchema,
      });

      return result as unknown as BookAgendaSlotAdminResponse;
    });
};

const useBookAgendaSlotAdminMutation = (): UseMutationResult<
  BookAgendaSlotAdminResponse,
  HttpErrors,
  BookAgendaSlotAdminPayload
> => {
  const { t } = useTranslation();
  const slotBookingConflitErrorHandler = useSlotBookingConflitErrorHandler();

  return useMutation({
    mutationFn: bookAgendaSlotAdmin,
    onSuccess() {
      toast.success(t('http.book-agenda-slot.success'));
    },
    onError(error) {
      const payload = getErrorPayload({
        error,
        type: 'agenda_slot_booking_conflict',
      });
      if (payload) {
        slotBookingConflitErrorHandler.handle(payload);
        return;
      }
    },
  });
};

export default useBookAgendaSlotAdminMutation;
